import { checkOutcome, CheckOutcome } from "../status/checkOutcome";

/** Sort order of outcomes: failures, then no data, then passes. @public */
export const OUTCOME_ORDER: Readonly<Record<CheckOutcome, number>> =
  Object.freeze({ fail: 0, noData: 1, pass: 2 });

/** A check definition with its result for one entity. @public */
export interface JoinedCheck<C, R> {
  definition: C;
  /** The definition with the result's check merged over it: jsonfc merges success or failure metadata there. */
  effective: C;
  result?: R;
  outcome: CheckOutcome;
}

/**
 * Every defined check with its result, then the checks that ran but are not
 * defined (they still count).
 * @public
 */
export function joinChecksAndResults<
  C extends { id: string },
  R extends { check: C; result: unknown }
>(checks: ReadonlyArray<C>, results: ReadonlyArray<R>): JoinedCheck<C, R>[] {
  const resultById = new Map(results.map((r) => [r.check.id, r]));
  const defined = new Set(checks.map((c) => c.id));
  const ran = results.map((r) => r.check).filter((c) => !defined.has(c.id));
  return [...checks, ...ran].map((definition) => {
    const result = resultById.get(definition.id);
    const joined: JoinedCheck<C, R> = {
      definition,
      effective: result ? { ...definition, ...result.check } : definition,
      outcome: checkOutcome(result?.result),
    };
    if (result) joined.result = result;
    return joined;
  });
}
